"use client";

import React, { useRef } from "react";
import { sanitizeHtml } from "@repo/utils/common";
import { pickLocalizedBodyHtml } from "../../section/post-body/tiptap-render";
import { Lightbox } from "../../section/post-body/Lightbox";

/**
 * Undo double-encoded HTML entities. WP-imported posts often store
 * `&amp;nbsp;` (or `&amp;amp;`, etc.) where the source had `&nbsp;`
 * — the entity passed through an encoding step twice. The browser
 * then renders the literal text `&nbsp;` instead of a space.
 *
 * Strategy:
 *   1. Decode common named entities and numeric refs that got
 *      double-encoded back to single-encoded form (browser handles
 *      the rest).
 *   2. Also collapse stray literal `&nbsp;` runs that survived the
 *      decoder (some sources keep them as plain text).
 *
 * Conservative — only touches the entities we've seen in the wild
 * to avoid mangling intentional `&amp;` inside e.g. URLs.
 */
function cleanLegacyHtml(html: string): string {
  if (!html) return html;
  let out = html;
  // `&amp;nbsp;` → `&nbsp;` (and similar named entities). Browser
  // then decodes the singly-encoded form normally on render.
  out = out.replace(
    /&amp;(nbsp|amp|quot|apos|lt|gt|hellip|mdash|ndash|laquo|raquo|copy|reg|trade|deg);/gi,
    "&$1;",
  );
  // `&amp;#NNN;` → `&#NNN;` (double-encoded numeric refs).
  out = out.replace(/&amp;#(\d+);/g, "&#$1;");
  // Hex form `&amp;#x..;` → `&#x..;`.
  out = out.replace(/&amp;#x([0-9a-f]+);/gi, "&#x$1;");
  return out;
}

interface ArticleBodyProps {
  /** Per-language body — `body.en`, `body.mm`. Either Tiptap PM JSON or HTML. */
  body?: { en?: unknown; mm?: unknown } | null;
  contentFormat?: "json" | "html" | "plain" | "markdown";
  currentLanguage?: "en" | "mm";
}

/**
 * Article body renderer — picks the language, converts Tiptap JSON to
 * HTML (or passes through HTML / string content), and injects it into
 * a `prose`-styled article. Reuses the same `tiptapJsonToHtml` walker
 * used by `PostBodySection` so all post types render the same way.
 *
 * Returns `null` when the body has no content for either language —
 * caller decides whether to show "no content yet" placeholder.
 */
export function ArticleBody({
  body,
  contentFormat = "json",
  currentLanguage = "en",
}: ArticleBodyProps) {
  // Declared before the early returns so hook order stays stable. The
  // `Lightbox` binds a delegated click listener to this container and
  // zooms any `img[data-lightbox="true"]` the renderer emitted.
  const articleRef = useRef<HTMLElement | null>(null);
  if (!body) return null;
  // Bidirectional language fallback: if the current language's body is empty
  // (blank string OR empty Tiptap doc), show the other language — and vice-versa.
  const rawHtml = pickLocalizedBodyHtml(body, currentLanguage, contentFormat);
  if (!rawHtml) return null;

  // Strip double-encoded entities (`&amp;nbsp;` → `&nbsp;` etc.)
  // common in WP-imported posts. Applied to BOTH the Tiptap-
  // generated HTML and the raw-HTML branch so legacy + new content
  // both render cleanly.
  const html = cleanLegacyHtml(rawHtml);

  if (!html) return null;

  return (
    <>
    <article
      ref={articleRef}
      // All article-body typography (line-height, text-shadow) is
      // managed by the `[data-post-body="article"]` CSS block in
      // `globals.css` — Tailwind's descendant-variant utilities
      // (`[&_p]:leading-[…]`) couldn't reliably beat prose's
      // per-tag specificity, and arbitrary-value text-shadow
      // utilities silently dropped declarations. A real CSS file
      // with explicit per-tag rules wins both battles cleanly. See
      // the comment block in `globals.css` for the full rationale.
      className="prose prose-neutral dark:prose-invert max-w-none w-full"
      // `data-rich-html` activates the explicit typography in globals.css
      // (heading scale, lists, blockquote, tables, code). The `prose-*`
      // classes are effectively no-ops here — the `@tailwindcss/typography`
      // plugin isn't loadable in this monorepo's pnpm layout, so without
      // this attribute Preflight reset headings to body size (h1/h2 looked
      // unstyled). `data-post-body="article"` still layers article-only
      // line-height + text-shadow on top.
      data-rich-html
      data-post-body="article"
      dangerouslySetInnerHTML={{ __html: sanitizeHtml(html) }}
    />
      <Lightbox containerRef={articleRef} />
    </>
  );
}

export default ArticleBody;
